import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Loader2, LogOut, Gift, Award } from "lucide-react";
import AddressesManager from "../components/AddressesManager";

export default function Account() {
  const { user, loyalty, logout, loading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    if (!loading && !user) { nav("/"); return; }
    if (user) {
      api.get("/auth/me/orders").then(({ data }) => setOrders(data.orders || [])).finally(() => setBusy(false));
    }
  }, [user, loading, nav]);

  if (loading || !user) return <div className="min-h-screen bg-ink flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  const tierTarget = loyalty?.next_tier_target || 10;
  const progress = Math.min(100, ((loyalty?.order_count_cycle || 0) / tierTarget) * 100);

  return (
    <div className="min-h-screen bg-ink text-cream">
      <header className="border-b border-border bg-black/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="font-heading text-cream uppercase tracking-tight text-xl">
            <span className="text-gold">LSK</span> · Your Account
          </Link>
          <button onClick={() => { logout(); nav("/"); }} className="btn-outline-poster !px-3 !py-2 !text-sm" data-testid="account-logout-btn">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <section className="poster-card p-6">
          <div className="font-heading uppercase tracking-tight text-cream text-3xl" data-testid="account-name">Hi, {user.name}</div>
          <div className="text-cream/60 text-sm mt-1">{user.email} · {user.phone}</div>
        </section>

        <section className="poster-card p-6">
          <div className="flex items-center gap-2 font-heading uppercase tracking-widest text-gold text-sm">
            <Award className="h-4 w-4" /> Loyalty Tracker
          </div>
          <div className="mt-3 flex items-baseline justify-between">
            <div>
              <div className="font-heading text-cream text-5xl leading-none" data-testid="account-cycle-count">{loyalty?.order_count_cycle ?? 0}</div>
              <div className="text-cream/60 text-sm mt-1">orders this cycle</div>
            </div>
            <div className="text-right">
              <div className="font-heading uppercase tracking-tight text-cream/80 text-lg">Next reward</div>
              <div className="text-gold font-heading uppercase tracking-tight text-xl">{loyalty?.next_tier_label}</div>
              <div className="text-cream/60 text-xs">{loyalty?.remaining_to_next} more orders</div>
            </div>
          </div>
          <div className="mt-4 h-3 bg-border">
            <div className="h-3 bg-gold transition-all" style={{ width: `${progress}%` }} data-testid="account-progress-bar" />
          </div>
          <div className="mt-2 flex justify-between text-cream/50 text-xs font-heading uppercase tracking-widest">
            <span>0</span>
            <span>10 → Free Wings</span>
            <span>25 → Free Combo</span>
          </div>
          <div className="mt-3 text-xs text-cream/60">Lifetime orders: <span className="text-gold font-heading">{loyalty?.lifetime_orders ?? 0}</span></div>

          {(loyalty?.pending_rewards?.length || 0) > 0 && (
            <div className="mt-5">
              <div className="font-heading uppercase tracking-widest text-gold text-xs">Available Rewards</div>
              <div className="mt-2 space-y-2">
                {loyalty.pending_rewards.map((r) => (
                  <div key={r.id} className="border border-gold/50 p-3 flex items-center gap-3 bg-gold/5" data-testid={`pending-reward-${r.id}`}>
                    <Gift className="h-5 w-5 text-gold" />
                    <div className="flex-1">
                      <div className="font-heading uppercase tracking-tight text-cream text-lg leading-none">{r.label}</div>
                      <div className="text-cream/60 text-xs mt-1">Earned {new Date(r.earned_at).toLocaleDateString("en-GB")}</div>
                    </div>
                    <Link to="/" className="btn-poster !text-sm !px-4 !py-2">Use in Cart</Link>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        <AddressesManager />

        <section className="poster-card p-6">
          <div className="font-heading uppercase tracking-tight text-cream text-2xl">Order History</div>
          {busy ? (
            <div className="mt-4 flex items-center gap-2 text-cream/60"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : orders.length === 0 ? (
            <div className="mt-4 text-cream/60 text-sm" data-testid="no-orders">No orders yet — your first counts toward loyalty!</div>
          ) : (
            <ul className="mt-4 space-y-3" data-testid="account-orders-list">
              {orders.map((o) => (
                <li key={o.id} className="border border-border p-4 grid sm:grid-cols-12 gap-3" data-testid={`account-order-${o.id}`}>
                  <div className="sm:col-span-3">
                    <div className="font-heading text-gold text-2xl">£{o.total_gbp.toFixed(2)}</div>
                    <div className="text-cream/50 text-xs">{new Date(o.created_at).toLocaleString("en-GB")}</div>
                    <span className={`inline-block mt-1 px-2 py-0.5 text-[10px] font-heading uppercase tracking-widest ${o.payment_status === "paid" ? "bg-green-700 text-white" : o.payment_status === "cash_on_delivery" ? "bg-amber-600 text-black" : "bg-zinc-700 text-white"}`}>
                      {o.payment_status}
                    </span>
                  </div>
                  <div className="sm:col-span-6 text-sm">
                    <ul>
                      {o.items.map((it, i) => (
                        <li key={i} className="text-cream/80 flex justify-between">
                          <span>{it.quantity}× {it.name}</span>
                          <span className="text-cream/50">£{it.line_total.toFixed(2)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="sm:col-span-3 text-sm">
                    <div className="font-heading uppercase tracking-widest text-cream/70 text-xs">Status</div>
                    <div className="text-gold font-heading uppercase tracking-tight">{o.status.replace(/_/g, " ")}</div>
                    <div className="text-cream/50 text-xs mt-1">{o.delivery.address_line1}, {o.delivery.postcode}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
